import { CURRENCY, buildOfferUrl } from '@apm/contracts';
/**
 * 상품 어댑터 (v23 O8). **`vast-ad.ts` 와 같은 자리에 같은 모양으로 선다.**
 *
 * **코어에 둘 수 없는 이유는 하나다** — HTTP 다. `tsconfig.core.json` 이
 * `lib: ES2022`·`types: []` 로 `fetch` 를 막는다 (절대 규칙 6).
 *
 * **`NetworkAdapter` 를 넓히지 않았다** (절대 규칙 36) — 넓히면 iOS/Android 이식의 근거가
 * 약해진다. 이것은 **갈아 끼울 수 있는 어댑터**이지 넓어진 계약이 아니다.
 *
 * ── 광고와 갈리는 자리 하나 ────────────────────────────────
 *
 * `buildTagUrl` 은 **이 폴더에 있는데** `buildOfferUrl` 은 **`@apm/contracts` 에 있다.**
 * 부르는 자리가 광고는 리시버 하나였고, 상품은 **리시버와 서버측 미리보기 둘**이기
 * 때문이다 (`docs/47` §5.1 · §6.3). 두 벌이 되면 어긋남이
 * **「미리보기로 본 것과 실제로 나가는 것이 다르다」**이며 **아무 오류도 내지 않는다.**
 *
 * ── 파서는 여기 있다 ──────────────────────────────────────
 *
 * **상품에는 VAST 같은 표준이 없다**(`docs/47` D14). 그래서 갈리는 자리를 **순수 함수
 * 둘**로 가둔다 — `parseOffers()` 와 `guardItem()`. **제휴사가 자기 포맷을 강요하는 날
 * 여는 것은 이 파일이 아니라 브로커의 `normalize.ts`** 다.
 */
// ---------------------------------------------------------------------------
// 1. 둘째 그물 — 브로커가 §7.4 를 어겼을 때
// ---------------------------------------------------------------------------
/**
 * 응답 하나를 `OfferItem` 으로 좁힌다. **버릴 것은 `null` 이다. 순수 함수다.**
 *
 * ── 브로커가 이미 막는데 여기서 또 재는 이유 ────────────────
 *
 * **브로커는 우리 것이지만 상용 제휴사로 바꾸는 날 그 사이에 다른 것이 설 수 있다**
 * (`docs/47` §8.7). 그때 남는 그물이 이것이다.
 *
 * 그리고 v21 이 배운 것 하나 — **폴백이 조용하면 원인이 화면에 드러나지 않는다.**
 * 그래서 강등할 때 `handoffSource` 를 함께 바꾼다.
 *
 * **막는 넷이 브로커의 `guard-url.ts` 와 같은 넷이다** (§7.4).
 */
export function guardItem(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const value = raw;
    // **`offerId` 가 없으면 나를 방법이 없다** — 폰이 사본을 읽을 주소가 그것이다.
    const offerId = str(value['offerId']);
    if (offerId === null)
        return null;
    // ② **웹 랜딩이 https 가 아니면 그 상품을 버린다.** QR 로 스캔한 폰과 PC 에서 열리지
    // 않고, **그 둘은 데모에서 잘 안 밟는 경로**라 TV 에서는 아무 증상이 없다.
    const landingUrl = str(value['landingUrl']);
    if (landingUrl === null || !isHttps(landingUrl))
        return null;
    // ③ **딥링크에 스킴이 없으면 웹 랜딩으로 대신하고 그 사실을 남긴다.** 빈 값이나 상대
    // URL 은 폰에서 「자기 오리진」이 되어 **앱이 자기 시작 게이트로 간다**(v21/F13).
    const rawDeepLink = str(value['deepLink']);
    const partnerDeepLink = rawDeepLink !== null && hasScheme(rawDeepLink);
    const deepLink = partnerDeepLink ? rawDeepLink : landingUrl;
    /*
     * ★ **강등 판정은 우리가 다시 한다 — 다만 한 방향으로만 이긴다.**
     *
     * 우리 판정은 **끌어내릴 수는 있어도 끌어올리지 못한다.** 브로커가 준
     * `handoffSource` 가 `'landing'` 이면 그대로 `'landing'` 이고, `'partner'` 라고
     * 적혀 있어도 딥링크에 스킴이 없으면 우리가 `'landing'` 으로 내린다.
     *
     * ★★ **한 방향인 것이 이 줄의 전부다** (v23/O16 이 실기로 찾았다).
     *
     * 브로커는 딥링크가 없을 때 **그 칸을 웹 랜딩으로 채워서 보낸다**(`guard-url.ts` ③).
     * 그래서 여기 도착한 `deepLink` 에는 **언제나 스킴이 있고**, 다시 계산하면 결과가
     * **영원히 `'partner'`** 다 — 그 순간 **강등의 유일한 흔적이 지워진다.** 화면도
     * 하네스도 「딥링크로 열린다」고 말하는데 실제로는 웹 랜딩이고, `docs/47` §7.4 가
     * 「강등이 조용해서는 안 된다」로 막으려던 상태가 **바로 그것**이다.
     *
     * 둘째 그물의 뜻은 「브로커를 믿지 않는다」이지 「브로커가 본 것을 잊는다」가 아니다.
     */
    const brokerDemoted = value['handoffSource'] === 'landing';
    const handoffSource = partnerDeepLink && !brokerDemoted ? 'partner' : 'landing';
    return {
        offerId,
        title: str(value['title']) ?? '',
        // ④ **금액은 정수.** 아니면 0 이고 **화면이 가격을 숨긴다** — 틀린 가격을 그리는
        // 것보다 안 그리는 것이 낫다.
        price: intOrZero(value['price']),
        // 통화는 계약이 한 값뿐이다. 제휴사가 무엇을 보내든 우리 축은 원이다.
        currency: CURRENCY,
        // ① **썸네일이 https 가 아니면 빈 문자열.** 그 상품을 **버리지는 않는다** —
        // 상품명과 가격이 본체이고 화면이 자리표시자를 그린다.
        thumbnailUrl: httpsOrEmpty(value['thumbnailUrl']),
        deepLink,
        landingUrl,
        partnerId: str(value['partnerId']) ?? '',
        partnerName: str(value['partnerName']) ?? '',
        couponCode: str(value['couponCode']),
        tracking: guardTracking(value['tracking']),
        matchedCategoryId: intOrZero(value['matchedCategoryId']),
        handoffSource,
    };
}
/**
 * 응답 본문 → `OfferItem[]`. **순수 함수다.**
 *
 * **모양이 아니면 빈 배열이다 — 던지지 않는다.** 그것이 no fill 과 같은 길이고, 코어는
 * 셋을 구분하지 않는다 (`docs/47` §8.6).
 *
 * **버려진 후보는 조용히 사라지고 나머지는 나간다** — 하나가 나쁘다고 전부 버리지 않는다.
 */
export function parseOffers(json) {
    if (typeof json !== 'object' || json === null)
        return [];
    const offers = json.offers;
    if (!Array.isArray(offers))
        return [];
    const out = [];
    for (const raw of offers) {
        const item = guardItem(raw);
        if (item !== null)
            out.push(item);
    }
    return out;
}
export class WebOfferAdapter {
    fetchImpl;
    sendPixel;
    constructor(opts = {}) {
        this.fetchImpl = opts.fetchImpl ?? ((...args) => fetch(...args));
        this.sendPixel = opts.sendPixel ?? defaultSendPixel;
    }
    /**
     * **타임아웃을 호출자가 준다** — 코어가 `OFFER_REQUEST_TIMEOUT_MS`(5초)를 넘긴다.
     * 어댑터가 상수로 박으면 그 값이 **리드 타임과의 관계에서 떨어져 나가고**, 그 관계를
     * 지키는 것은 `offer.test.ts` 의 단언 한 줄뿐이다.
     *
     * **초과 뒤 늦게 온 응답은 `AbortController` 로 끊는다.** 코어에도 늦은 응답 가드가
     * 있지만(§8.4 의 ★) **둘은 다른 것을 막는다** — 여기는 소켓을, 저기는 화면을.
     *
     * ★ **실패는 빈 배열이다. 던지지 않는다** — 상품이 안 뜨는 것은 시청자에게 사고가
     * 아니고, 코어가 `emit('error')` 하면 **멀쩡히 재생 중인 채널에 오류 UI 가 뜬다**
     * (`resolveNextUrl` 의 `catch` 가 그 선례다).
     */
    async requestOffer(req, opts) {
        // 브로커 주소가 없으면 부를 곳이 없다. 서버가 그때 정책을 `null` 로 주므로 여기
        // 닿을 일이 사실상 없지만, **닿았을 때 `/offers` 를 상대 URL 로 부르지 않게** 막는다.
        if (req.brokerUrl.trim() === '')
            return [];
        const controller = new AbortController();
        const timer = setTimeout(() => {
            controller.abort();
        }, opts.timeoutMs);
        try {
            const response = await this.fetchImpl(buildOfferUrl(req), {
                signal: controller.signal,
                // 브로커에는 자격이 없다 (`docs/47` §6.1). 쿠키를 싣지 않는다.
                credentials: 'omit',
                cache: 'no-store',
            });
            if (!response.ok)
                return [];
            return parseOffers(await response.json());
        }
        catch {
            // 타임아웃(abort) · 네트워크 · **혼합 콘텐츠 차단** — 셋이 같은 길이다.
            // 셋째가 이 확장에서 가장 그럴듯한 실패다 (`docs/47` §14 의 20).
            return [];
        }
        finally {
            clearTimeout(timer);
        }
    }
    /**
     * 트래킹 픽셀. **실패해도 조용하다** — 전송 실패가 재생을 방해해서는 안 된다는
     * `EventQueue` 의 규율 그대로다 (절대 규칙 55 승계).
     */
    track(urls) {
        for (const url of urls) {
            try {
                this.sendPixel(url);
            }
            catch {
                // 상품이 안 세지는 것보다 재생이 끊기는 것이 나쁘다.
            }
        }
    }
}
/**
 * `Image` 로 쏜다. `fetch` 가 아닌 이유 — **응답을 기다릴 이유가 없고**, 픽셀은 CORS 를
 * 요구하지 않는다(브로커가 302 로 넘기는 곳은 제휴사이고 우리 것이 아니다).
 */
function defaultSendPixel(url) {
    const pixel = new Image();
    pixel.src = url;
}
// ---------------------------------------------------------------------------
// 좁히는 함수들 — 전부 순수하다
// ---------------------------------------------------------------------------
function str(value) {
    return typeof value === 'string' && value !== '' ? value : null;
}
/** **문자열 숫자를 받아 준다** — 제휴사 JSON 에서 흔하고, 0 으로 만들면 가격이 사라진다 */
function intOrZero(value) {
    if (typeof value === 'number')
        return Number.isInteger(value) && value >= 0 ? value : 0;
    if (typeof value === 'string' && /^\d+$/.test(value)) {
        const parsed = Number(value);
        return Number.isSafeInteger(parsed) ? parsed : 0;
    }
    return 0;
}
function isHttps(value) {
    try {
        return new URL(value).protocol === 'https:';
    }
    catch {
        return false;
    }
}
function hasScheme(value) {
    try {
        new URL(value);
        return true;
    }
    catch {
        return false;
    }
}
function httpsOrEmpty(value) {
    const text = str(value);
    return text !== null && isHttps(text) ? text : '';
}
/** 트래킹은 **문자열 배열 둘**이다. 모양이 아니면 빈 배열 — 쏠 것이 없을 뿐이다 */
function guardTracking(value) {
    if (typeof value !== 'object' || value === null)
        return { impression: [], click: [] };
    const tracking = value;
    return { impression: urlList(tracking.impression), click: urlList(tracking.click) };
}
function urlList(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((entry) => typeof entry === 'string' && entry !== '');
}
//# sourceMappingURL=http-offer.js.map